import { ANONYMOUS_USER_EMAIL, AssetStatus } from '@thehubz/shared/model';
import { AssetRole } from './core-roles';
import { GlobalRole } from './global-role.enum';

export interface UserLike {
  _id: string;
  firstName?: string;
  lastName?: string;
  salutation?: string;
  email: string;
}

const ADMIN_ALL = 'admin.all';
export const API_ALL = 'api.all';

export const ACCESS_INTERNAL = 'internal';
export const ACCESS_DRAFT = 'draft';

export const buildUserAccessKey = (id: string) => `user.${id}`;

export class UserAccess {
  api = false;

  id?: string;
  name?: string;
  salutation?: string;
  email!: string;
  branding?: string; // the user can login from a branded client

  // vorwerk specific
  targetGroups = ['All'];
  exchange?: { roles: number[] };

  // if this session is impersonated, it is the user id
  impersonated?: string;

  private readonly _roles: Set<string>;
  /** Map with key as accessKey and value as array of roles */
  private readonly _accessRoles: Map<string, string[]>;
  /** All global roles */
  private _globalRoles: string[];
  /** All available access keys */
  private _accessKeys: string[];

  private _userAccessKey?: string;

  private _visibleStatus: AssetStatus[] = [];

  constructor(globalRoles?: string[], accessKeys?: [string, string[]][]) {
    this._globalRoles = globalRoles ?? [];
    this._accessRoles = new Map<string, string[]>(accessKeys);
    this._roles = new Set<string>(globalRoles);
    accessKeys?.map((entry) => entry[1]).forEach((roles) => roles.forEach((role) => this._roles.add(role)));
    this._accessKeys = [...this._accessRoles.keys()];
  }

  /**
   * Mainly for backend we can optional add a user data here.
   *
   * @param user A user object
   */
  setUser(user: UserLike): void {
    this.id = user._id.toString();
    this.name = `${user.firstName} ${user.lastName}`;
    this.email = user.email;
    this.salutation = user.salutation;

    this._userAccessKey = buildUserAccessKey(user._id);
  }

  addRole(role: string) {
    this._roles.add(role);
  }

  setAccessRoles(key: string, roles: string[]) {
    this._accessRoles.set(key, roles);
  }

  get accessRoles() {
    return [...this._accessRoles];
  }

  /**
   * Collect all permission keys for the data.
   *
   * @param accessKeys The data access keys e.g. from a task, project or asset
   * @returns Array of permission keys
   */
  getAccessKeysRoles(...accessKeys: string[]): string[] {
    const roles = new Set<string>();
    accessKeys.forEach((key) => this._accessRoles.get(key)?.forEach((keyRole) => roles.add(keyRole)));
    return [...roles];
  }

  /**
   * Check wether the given role is available for the access-keys provided.
   *
   * @param accessKeys Access keys to check for
   * @param role The required role
   * @returns true, if the role is available
   */
  hasRoleAccess(role: string, accessKeys: string[]): boolean {
    if (this.hasRole(ADMIN_ALL) || this.hasRole(API_ALL)) {
      return true;
    }

    for (const element of accessKeys) {
      if (element === this._userAccessKey || element === ACCESS_INTERNAL || this._accessRoles.get(element)?.includes(role)) {
        return true;
      }
      if (element === ACCESS_DRAFT && (this.hasRole(GlobalRole.admin) || this.hasRole(AssetRole.accessDraft))) {
        return true;
      }
    }
    return false;
  }

  checkRole(role: string): boolean {
    if (role.startsWith('!')) {
      return !this._roles.has(role.substring(1));
    }
    return this._roles.has(role);
  }

  hasRole(role: string): boolean {
    return this._roles.has(role);
  }

  isAdmin() {
    return this._globalRoles.includes(GlobalRole.admin) || this.hasRole(GlobalRole.admin);
  }

  isAnonymous() {
    return this.email === ANONYMOUS_USER_EMAIL;
  }

  get accessKeys() {
    return [...this._accessKeys];
  }

  __setAccessKeys(accessKeys: string[]) {
    this._accessKeys = accessKeys;
  }

  get globalRoles() {
    return [...this._globalRoles];
  }

  __setGlobalRoles(globalRoles: string[]) {
    this._globalRoles = globalRoles;
    this.setVisibleStatusFromRoles(globalRoles);
  }

  get useUserId() {
    return this.impersonated ?? this.id;
  }

  get ensureUserId() {
    if (!this.id) {
      throw Error('No user id available');
    }
    return this.id;
  }

  get visibleStatus() {
    return [...this._visibleStatus];
  }

  private setVisibleStatusFromRoles(roles: string[]) {
    this._visibleStatus = [];
    if (roles.includes(AssetRole.statusPublishedOnly)) {
      this._visibleStatus.push(AssetStatus.approved);
    } else {
      // set the default visible status
      this._visibleStatus.push(AssetStatus.approved, AssetStatus.uploaded);
      if (roles.includes(AssetRole.statusAll)) {
        this._visibleStatus.push(AssetStatus.rejected, AssetStatus.pending, AssetStatus.created, AssetStatus.expired);
      }
    }
    // add additional status based on roles
    if (roles.includes(AssetRole.statusDraft)) {
      this._visibleStatus.push(AssetStatus.draft);
    }
    if (roles.includes(AssetRole.statusUploadedAwaitingApproval)) {
      this._visibleStatus.push(AssetStatus.uploaded, AssetStatus.awaiting_approval);
    }
    if (roles.includes(AssetRole.statusArchived)) {
      this._visibleStatus.push(AssetStatus.archived);
    }
  }
}
